import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  convertToEwbanks,
  convertEwbanksToFrench,
  convertEwbanksToYDS,
  isYDSGrade
} from '@utils/gradeUtils.js';
import { formatAscentDateLong as formatDate } from '@utils/dateUtils.js';

const RouteSearch = ({ data }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

const formatDebugGrade = (route) => {
    if (route.vgrade !== null && route.vgrade !== undefined) {
      return `V${route.vgrade}`;
    }
    
    const rawGrade = route["Route Grade"];
    const ewbanks = convertToEwbanks(rawGrade);
    
    if (!isNaN(ewbanks)) {
      const yds = convertEwbanksToYDS(ewbanks);
      const french = convertEwbanksToFrench(ewbanks);
      return `${ewbanks} (YDS: ${yds || 'unknown'}, FR: ${french || 'unknown'})`;
    }
    
    return `failed to convert: ${rawGrade}`;
  };
  
  const filteredRoutes = data?.filter(route => 
    route["Route Name"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route["Crag Name"]?.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 25) ?? [];

  return (
    <div className="relative flex-1">
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowSearchResults(true);
        }}
        placeholder="Search routes..."
        className="w-full h-9 px-8 rounded border text-sm"
      />
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
      {searchTerm && (
        <button 
          onClick={() => {
            setSearchTerm('');
            setShowSearchResults(false);
          }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <X size={16} />
        </button>
      )}
      
      {/* Search Results Panel */}
      {showSearchResults && searchTerm && filteredRoutes.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[400px] overflow-y-auto z-50">
          <div className="divide-y divide-gray-200">
            {filteredRoutes.map((route, index) => (
              <div 
                key={`${route['Ascent ID']}-${index}`}
                className="p-2 hover:bg-gray-50"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900 truncate">{route["Route Name"]}</h3>
                      <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                        {formatDate(route["Ascent Date"])}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 truncate mt-0.5">
                      {route["Crag Path"]}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                      <span>{route["Route Grade"]}</span>
                      <span>•</span>
                      <span>{route["Route Gear Style"]}</span>
                      <span>•</span>
                      <span>{route["Ascent Type"]}</span>
                    </div>
                    {route["Comment"] && (
                      <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                        {route["Comment"]}
                      </p>
                    )}
                    <div className="mt-1 text-xs">
                      <span className="text-gray-500">Debug:</span>
                      <span className="ml-1 font-mono text-gray-400">
                        raw: {route["Route Grade"]}, 
                        converted: {formatDebugGrade(route)}, 
                        clean: {route.isClean ? 'yes' : 'no'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteSearch;